import { useState, useEffect, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { Search, Trash2, Plus, Printer, FileText, Send, X, Pencil } from "lucide-react";
import { fetchJSON } from "@/lib/api";
import { fetchEmployees } from "@/lib/employees";
import { fuelProductLabel } from "@/lib/fuelProducts";
import { useFuelRates } from "@/hooks/useFuelRates";
import { PrintService } from "@/utils/PrintService";
import { formatDate, formatDateTime } from "@/lib/dateUtils";

interface CreditItem {
  id: string;
  organization: string;
  vehicleNo: string;
  product: string;
  price: string;
  creditAmt: string;
  discount: string;
  qty: string;
  indentNo: string;
  miscCharges: string;
  billNo: string;
  employee: string;
  effect: string;
  effectTo: string;
  description: string;
  meterRead: string;
  mileage: string;
  fileName: string;
  uploadedFile: string | null;
}

// Local helper replaced by centralized formatDateTime from dateUtils

export default function CreditSale() {
  const queryClient = useQueryClient();

  // State
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [shift, setShift] = useState<"S-1" | "S-2">("S-1");
  const [advance, setAdvance] = useState<string>("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [balances, setBalances] = useState<Record<string, number>>({});

  // Fetch outstanding balance for a credit customer organization
  const fetchOutstandingBalance = async (customerId: string) => {
    if (!customerId || balances[customerId] !== undefined) return;
    try {
      const response = await fetch(`/api/credit-customers/${customerId}/outstanding`, { credentials: 'include' });
      const result = await response.json();
      if (result.ok) {
        setBalances(prev => ({
          ...prev,
          [customerId]: result.outstanding_balance
        }));
      }
    } catch (e) {
      console.error("Failed to fetch outstanding balance", e);
    }
  };
  // Add Party Modal State
  const [isAddPartyOpen, setIsAddPartyOpen] = useState(false);
  const [newParty, setNewParty] = useState({
    orgName: "",
    custName: "",
    phone: "",
    email: "",
    creditLimit: ""
  });

  // Auto-fill Logic
  const [autoFillOrg, setAutoFillOrg] = useState<string>("");
  const [autoFillProd, setAutoFillProd] = useState<string>("");
  const [autoFillEmp, setAutoFillEmp] = useState<string>("");

  // Dynamic Array State
  const [creditItems, setCreditItems] = useState<CreditItem[]>([{
    id: "1",
    organization: "",
    vehicleNo: "",
    product: "",
    price: "",
    creditAmt: "",
    discount: "",
    qty: "",
    indentNo: "",
    miscCharges: "",
    billNo: "",
    employee: "",
    effect: "No",
    effectTo: "",
    description: "",
    meterRead: "",
    mileage: "",
    fileName: "",
    uploadedFile: null
  }]);

  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  // Search/Filter
  const [searchFrom, setSearchFrom] = useState<string>("");
  const [searchTo, setSearchTo] = useState<string>("");
  const [searchOrg, setSearchOrg] = useState<string>("");
  const [filterText, setFilterText] = useState<string>("");

  // Queries
  const { data: customers } = useQuery({
    queryKey: ["/api/credit-customers"],
    queryFn: async () => (await fetchJSON('/api/credit-customers')).json().then(res => res.rows || [])
  });

  // Fuel products with resolved prices for selected date (with historical fallback if not set for today)
  const { rates: fuelRates, fuelProducts, getRateById } = useFuelRates({ date, onlySelectedDate: false });

  const { data: employees } = useQuery({
    queryKey: ["/api/employees"],
    queryFn: fetchEmployees,
  });

  const { data: creditSalesData, refetch } = useQuery({
    queryKey: ["/api/credit-sales"],
    queryFn: async () => (await fetchJSON('/api/credit-sales')).json().then(res => res.rows || [])
  });

  const creditSalesRows = creditSalesData || [];

  // Automatically refresh prices and recalculate totals/quantities when date or rates change
  useEffect(() => {
    setCreditItems(prev => prev.map(item => {
      if (!item.product) return item;
      const rate = getRateById(item.product);
      if (rate != null) {
        const newItem = { ...item, price: String(rate) };
        const q = parseFloat(newItem.qty) || 0;
        const p = parseFloat(newItem.price) || 0;
        const amt = parseFloat(newItem.creditAmt) || 0;
        if (q > 0) {
          newItem.creditAmt = (q * p).toFixed(3);
        } else if (amt > 0) {
          newItem.qty = (amt / p).toFixed(3);
        }
        return newItem;
      }
      return item;
    }));
  }, [date, fuelRates, getRateById]);

  useEffect(() => {
    creditItems.forEach(item => {
      if (item.organization && balances[item.organization] === undefined) {
        fetchOutstandingBalance(item.organization);
      }
    });
  }, [creditItems]);

  useEffect(() => {
    if (autoFillOrg && balances[autoFillOrg] === undefined) {
      fetchOutstandingBalance(autoFillOrg);
    }
  }, [autoFillOrg]);

  const filteredCreditSalesRows = useMemo(() => {
    let result = creditSalesRows;

    // Filter by Org
    if (searchOrg) {
      result = result.filter((row: any) => {
        const rowCustId = row.creditCustomerId || row.credit_customer_id;
        return rowCustId === searchOrg || String(rowCustId) === String(searchOrg);
      });
    }

    // Filter by Search From Date
    if (searchFrom) {
      const fromDate = new Date(searchFrom);
      fromDate.setHours(0, 0, 0, 0);
      result = result.filter((row: any) => {
        if (!row.sale_date) return false;
        const rowDate = new Date(row.sale_date);
        rowDate.setHours(0, 0, 0, 0);
        return rowDate >= fromDate;
      });
    }

    // Filter by Search To Date
    if (searchTo) {
      const toDate = new Date(searchTo);
      toDate.setHours(23, 59, 59, 999);
      result = result.filter((row: any) => {
        if (!row.sale_date) return false;
        const rowDate = new Date(row.sale_date);
        rowDate.setHours(0, 0, 0, 0);
        return rowDate <= toDate;
      });
    }

    // Filter by filterText
    if (filterText) {
      const lowerSearch = filterText.toLowerCase();
      result = result.filter((row: any) => {
        const txnNo = (row.transaction_number || '').toLowerCase();
        const orgName = (row.organization_name || '').toLowerCase();
        const vehicleNo = (row.vehicle_number || '').toLowerCase();
        const productName = (row.product_name || '').toLowerCase();
        const empName = (row.employee_name || '').toLowerCase();
        const indentNo = (row.indent_no || '').toString().toLowerCase();
        const billNo = (row.bill_no || '').toString().toLowerCase();
        const desc = (row.description || '').toLowerCase();
        const shiftName = (row.shift || '').toLowerCase();

        return (
          txnNo.includes(lowerSearch) ||
          orgName.includes(lowerSearch) ||
          vehicleNo.includes(lowerSearch) ||
          productName.includes(lowerSearch) ||
          empName.includes(lowerSearch) ||
          indentNo.includes(lowerSearch) ||
          billNo.includes(lowerSearch) ||
          desc.includes(lowerSearch) ||
          shiftName.includes(lowerSearch)
        );
      });
    }

    return result;
  }, [creditSalesRows, searchFrom, searchTo, searchOrg, filterText]);

  // Update item function
  const updateItem = (id: string, field: keyof CreditItem, value: any) => {
    setCreditItems(prev => prev.map(item => {
      if (item.id !== id) return item;
      const newItem = { ...item, [field]: value };

      if (field === 'organization' && value) {
        fetchOutstandingBalance(value);
      }

      // Auto-fill price from daily rate when product is selected
      if (field === 'product' && value) {
        const autoPrice = getRateById(value);
        if (autoPrice != null) {
          newItem.price = String(autoPrice);
        }
      }

      const p = parseFloat(newItem.price) || 0;

      if (field === 'qty' || (field === 'price' && parseFloat(newItem.qty) > 0) || (field === 'product' && parseFloat(newItem.qty) > 0)) {
        const q = parseFloat(newItem.qty) || 0;
        newItem.creditAmt = (q > 0 && p > 0) ? (q * p).toFixed(3) : "";
      } else if (field === 'creditAmt' || (field === 'price' && parseFloat(newItem.creditAmt) > 0) || (field === 'product' && parseFloat(newItem.creditAmt) > 0)) {
        const amt = parseFloat(newItem.creditAmt) || 0;
        newItem.qty = (amt > 0 && p > 0) ? (amt / p).toFixed(3) : "";
      }

      return newItem;
    }));
  };

  const handleAddItem = () => {
    const autoPrice = autoFillProd ? getRateById(autoFillProd) : undefined;
    setCreditItems(prev => [...prev, {
      id: Date.now().toString(),
      organization: autoFillOrg || "",
      vehicleNo: "",
      product: autoFillProd || "",
      price: autoPrice != null ? String(autoPrice) : "",
      creditAmt: "",
      discount: "",
      qty: "",
      indentNo: "",
      miscCharges: "",
      billNo: "",
      employee: autoFillEmp || "",
      effect: "No",
      effectTo: "",
      description: "",
      meterRead: "",
      mileage: "",
      fileName: "",
      uploadedFile: null
    }]);
  };

  const handleRemoveItem = (id: string) => {
    if (creditItems.length > 1) {
      setCreditItems(prev => prev.filter(item => item.id !== id));
    }
  };

  const handleFileUpload = (id: string, file: File | undefined) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCreditItems(prev => prev.map(item => {
          if (item.id !== id) return item;
          return { ...item, uploadedFile: reader.result as string, fileName: file.name };
        }));
        toast({ title: "File uploaded successfully" });
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileInput = (id: string) => {
    const input = document.getElementById(`file-upload-credit-${id}`) as HTMLInputElement;
    if (input) input.click();
  };

  const handleAutoFillOrg = (val: string) => { 
    setAutoFillOrg(val); 
    fetchOutstandingBalance(val);
    setCreditItems(prev => prev.map(item => ({ ...item, organization: val })));
  };
  const handleAutoFillProd = (val: string) => {
    setAutoFillProd(val);
    const autoPrice = getRateById(val);
    setCreditItems(prev => prev.map(item => {
      const newItem = {
        ...item,
        product: val,
        price: autoPrice != null ? String(autoPrice) : item.price,
      };
      const q = parseFloat(newItem.qty) || 0;
      const p = parseFloat(newItem.price) || 0;
      const amt = parseFloat(newItem.creditAmt) || 0;
      if (q > 0) {
        newItem.creditAmt = (q * p).toFixed(3);
      } else if (amt > 0) {
        newItem.qty = (amt / p).toFixed(3);
      }
      return newItem;
    }));
  };
  const handleAutoFillEmp = (val: string) => { 
    setAutoFillEmp(val); 
    setCreditItems(prev => prev.map(item => ({ ...item, employee: val })));
  };

  // Mutations
  const createCreditSale = useMutation({
    mutationFn: async () => {
      for (const item of creditItems) {
        const payload = {
          sale_date: date,
          shift,
          credit_customer_id: item.organization || autoFillOrg,
          vehicle_number: item.vehicleNo,
          fuel_product_id: item.product || autoFillProd,
          quantity: parseFloat(item.qty) || 0,
          price_per_unit: parseFloat(item.price) || 0,
          discount: parseFloat(item.discount) || 0,
          advance: parseFloat(advance) || 0,
          misc_charges: parseFloat(item.miscCharges) || 0,
          indent_no: item.indentNo,
          bill_no: item.billNo,
          employee_id: item.employee || autoFillEmp,
          effect: item.effect,
          effect_to: item.effect === "Yes" ? item.effectTo : null,
          description: item.description,
          meter_reading: parseFloat(item.meterRead) || 0,
          mileage: parseFloat(item.mileage) || 0,
          image_url: item.uploadedFile
        };
        const res = await fetchJSON('/api/credit-sales', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const result = await res.json();
        if (!result.ok) throw new Error(result.error);
      }
      return true;
    },
    onSuccess: () => {
      toast({ title: "Success", description: "Credit sale saved!" });
      refetch();
      // Reset fields
      setCreditItems([{
        id: Date.now().toString(),
        organization: autoFillOrg || "",
        vehicleNo: "",
        product: autoFillProd || "",
        price: "",
        creditAmt: "",
        discount: "",
        qty: "",
        indentNo: "",
        miscCharges: "",
        billNo: "",
        employee: autoFillEmp || "",
        effect: "No",
        effectTo: "",
        description: "",
        meterRead: "",
        mileage: "",
        fileName: "",
        uploadedFile: null
      }]);
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" })
  });

  const updateCreditSale = useMutation({
    mutationFn: async () => {
      const item = creditItems[0];
      const payload = {
        sale_date: date,
        shift,
        credit_customer_id: item.organization || autoFillOrg,
        vehicle_number: item.vehicleNo,
        fuel_product_id: item.product || autoFillProd,
        quantity: parseFloat(item.qty) || 0,
        price_per_unit: parseFloat(item.price) || 0,
        discount: parseFloat(item.discount) || 0,
        advance: parseFloat(advance) || 0,
        misc_charges: parseFloat(item.miscCharges) || 0,
        indent_no: item.indentNo,
        bill_no: item.billNo,
        employee_id: item.employee || autoFillEmp,
        effect: item.effect,
        effect_to: item.effect === "Yes" ? item.effectTo : null,
        description: item.description,
        meter_reading: parseFloat(item.meterRead) || 0,
        mileage: parseFloat(item.mileage) || 0,
        image_url: item.uploadedFile
      };
      const res = await fetchJSON(`/api/credit-sales/${editingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await res.json();
      if (!result.ok) throw new Error(result.error);
      return true;
    },
    onSuccess: () => {
      toast({ title: "Success", description: "Credit sale updated successfully!" });
      setEditingId(null);
      refetch();
      // Reset fields
      setCreditItems([{
        id: Date.now().toString(),
        organization: autoFillOrg || "",
        vehicleNo: "",
        product: autoFillProd || "",
        price: "",
        creditAmt: "",
        discount: "",
        qty: "",
        indentNo: "",
        miscCharges: "",
        billNo: "",
        employee: autoFillEmp || "",
        effect: "No",
        effectTo: "",
        description: "",
        meterRead: "",
        mileage: "",
        fileName: "",
        uploadedFile: null
      }]);
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" })
  });

  const handleConfirm = () => {
    const isValid = creditItems.every(item => (item.organization || autoFillOrg) && (item.product || autoFillProd) && item.qty && item.price);
    if (!isValid) {
      toast({ title: "Validation Error", description: "Please fill required fields (Org, Product, Qty, Price) for all rows", variant: "destructive" });
      return;
    }
    if (editingId) {
      updateCreditSale.mutate();
    } else {
      createCreditSale.mutate();
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setAdvance("");
    setCreditItems([{
      id: Date.now().toString(),
      organization: autoFillOrg || "",
      vehicleNo: "",
      product: autoFillProd || "",
      price: "",
      creditAmt: "",
      discount: "",
      qty: "",
      indentNo: "",
      miscCharges: "",
      billNo: "",
      employee: autoFillEmp || "",
      effect: "No",
      effectTo: "",
      description: "",
      meterRead: "",
      mileage: "",
      fileName: "",
      uploadedFile: null
    }]);
  };

  const handleEditRow = (row: any) => {
    setEditingId(row.id);
    
    // Set date, shift, advance
    const dStr = row.sale_date ? new Date(row.sale_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
    setDate(dStr);
    setShift(row.shift === 'S-2' ? 'S-2' : 'S-1');
    setAdvance(String(row.advance || ''));
    
    // Map the row fields to the single creditItem in creditItems state
    setCreditItems([{
      id: row.id,
      organization: row.credit_customer_id || '',
      vehicleNo: row.vehicle_number || '',
      product: row.fuel_product_id || '',
      price: String(row.price_per_unit || ''),
      creditAmt: String(row.total_amount || ''),
      discount: String(row.discount || '0'),
      qty: String(row.quantity || ''),
      indentNo: row.indent_no || '',
      miscCharges: String(row.misc_charges || '0'),
      billNo: row.bill_no || '',
      employee: row.employee_id || '',
      effect: row.effect || 'No',
      effectTo: row.effect_to || '',
      description: row.description || '',
      meterRead: String(row.meter_reading || '0'),
      mileage: String(row.mileage || '0'),
      fileName: '',
      uploadedFile: row.image_url || null
    }]);
    
    // Scroll smoothly to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteRow = async (id: string) => {
    if (!confirm("Delete this credit sale?")) return;
    try {
      const res = await fetchJSON(`/api/credit-sales/${id}`, { method: 'DELETE' });
      const result = await res.json();
      if (result.ok) {
        toast({ title: "Success", description: "Credit sale deleted successfully" });
        refetch();
      } else {
        toast({ title: "Error", description: result.error || "Failed to delete", variant: "destructive" });
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (selectedRows.size === 0) return;
    if (!confirm("Delete selected rows?")) return;
    for (const id of selectedRows) {
      await fetchJSON(`/api/credit-sales/${id}`, { method: 'DELETE' });
    }
    setSelectedRows(new Set());
    refetch();
  };

  const handlePrint = () => {
    const rowsToPrint = selectedRows.size > 0
      ? creditSalesRows.filter((row: any) => selectedRows.has(row.id))
      : creditSalesRows;
    if (rowsToPrint.length === 0) {
      toast({ title: "Nothing to print", description: "No rows available to print", variant: "destructive" });
      return;
    }
    rowsToPrint.forEach((row: any) => {
      try {
        const d = row.sale_date ? new Date(row.sale_date) : null;
        const safeDate = d && !isNaN(d.getTime()) ? d.toISOString().slice(0, 10) : row.sale_date ? String(row.sale_date).slice(0, 10) : undefined;
        PrintService.printBill({
          billNo: row.transaction_number || row.id?.slice(-8),
          date: safeDate,
          productName: row.product_name,
          qty: row.quantity,
          price: row.price_per_unit,
          total: row.total_amount,
          discount: row.discount,
          indentNo: row.indent_no,
          customerName: row.organization_name,
          vehicleNo: row.vehicle_number,
          employeeName: row.employee_name
        });
      } catch (err: any) {
        console.error("Print error:", err);
        toast({ title: "Print Error", description: err?.message || "Failed to trigger print", variant: "destructive" });
      }
    });
  };

  // Add Party Mutation
  const addPartyMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        organization_name: newParty.orgName,
        representative_name: newParty.custName,
        phone_number: newParty.phone,
        email: newParty.email,
        credit_limit: parseFloat(newParty.creditLimit) || 0,
        isActive: true
      };
      const res = await fetchJSON('/api/credit-customers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await res.json();
      if (!result.ok) throw new Error(result.error);
      return result;
    },
    onSuccess: () => {
      toast({ title: "Success", description: "Party added successfully!" });
      setIsAddPartyOpen(false);
      setNewParty({ orgName: "", custName: "", phone: "", email: "", creditLimit: "" });
      queryClient.invalidateQueries({ queryKey: ["/api/credit-customers"] });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" })
  });

  const handleAddParty = () => {
    if (!newParty.orgName || !newParty.custName) {
      toast({ title: "Validation Error", description: "Org Name and Customer Name are required.", variant: "destructive" });
      return;
    }
    addPartyMutation.mutate();
  };

  return (
    <div className="min-h-screen bg-gray-100 p-2 font-sans text-sm">
      {/* Main Form Card */}
      <Card className="bg-[#3b66f5] text-white border-none shadow-lg mb-4 rounded-xl overflow-hidden">
        <div className="p-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Credit Sale</h2>
            <Button
              size="sm"
              className="bg-white text-blue-600 hover:bg-gray-100 rounded-full font-semibold"
              onClick={() => setIsAddPartyOpen(true)}
            >
              Add Party <Plus className="w-4 h-4 ml-1" />
            </Button>
          </div>

          {/* Top Row: Advance & Counts */}
          <div className="flex flex-wrap gap-4 items-center mb-4">
            <div className="flex items-center gap-2">
              <span className="bg-orange-500 text-white px-2 py-1 rounded text-xs font-bold uppercase">Advance</span>
              <Input placeholder="0.00" value={advance} onChange={e => setAdvance(e.target.value)} className="w-24 h-8 bg-white text-black border-none" />
            </div>
            {fuelRates.map((r) => (
              <div key={r.id} className="flex items-center gap-2">
                <span className="bg-gray-200 text-black px-2 py-1 rounded text-xs font-bold">
                  {r.code}
                </span>
                <span
                  title={r.source === 'daily' ? 'Daily Sale Rate' : r.source === 'lfr' ? 'LFR/KL Rate' : r.source === 'history' ? 'Last used price' : 'No price set'}
                  className={`min-w-[56px] text-center px-2 py-1 rounded text-xs font-bold ${
                    r.rate != null ? 'bg-cyan-500 text-white' : 'bg-white/30 text-white'
                  }`}
                >
                  {r.rate != null ? r.rate.toFixed(2) : '0'}
                </span>
              </div>
            ))}
          </div>

          {/* Date & Shift Row */}
          <div className="flex items-center gap-2 mb-4">
            <div className="bg-[#fbbf24] text-black font-bold px-4 py-2 rounded-l-md whitespace-nowrap">Choose Date</div>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-40 h-10 bg-white text-black rounded-none rounded-r-md border-none" />

            <div className="flex-1 bg-transparent border border-white/30 rounded-full px-4 py-2 flex items-center justify-center gap-6 ml-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="shift" checked={shift === 'S-1'} onChange={() => setShift('S-1')} className="accent-white w-4 h-4" />
                <span className="font-bold">S-1</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="shift" checked={shift === 'S-2'} onChange={() => setShift('S-2')} className="accent-white w-4 h-4" />
                <span className="font-bold">S-2</span>
              </label>
            </div>
          </div>

          {/* Auto-Fill Section */}
          <div className="border border-white/50 rounded-lg p-3 mb-6 relative mt-6">
            <span className="absolute -top-3 left-4 bg-[#3b66f5] px-2 text-xs font-bold uppercase">AUTO-FILL</span>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs mb-1 block">Organization</Label>
                <Select value={autoFillOrg} onValueChange={handleAutoFillOrg}>
                  <SelectTrigger className="bg-white text-black h-9 border-none"><SelectValue placeholder="Choose Organization" /></SelectTrigger>
                  <SelectContent>{customers?.map((c: any) => <SelectItem key={c.id} value={c.id}>{c.organization_name}</SelectItem>)}</SelectContent>
                </Select>
                {autoFillOrg && balances[autoFillOrg] !== undefined && (
                  <span className="text-[10px] text-amber-300 font-extrabold mt-1 block">
                    Pend Bal: {balances[autoFillOrg].toFixed(3)}
                  </span>
                )}
              </div>
              <div>
                <Label className="text-xs mb-1 block">Product</Label>
                <Select value={autoFillProd} onValueChange={handleAutoFillProd}>
                  <SelectTrigger className="bg-white text-black h-9 border-none"><SelectValue placeholder="choose Product" /></SelectTrigger>
                  <SelectContent>{fuelProducts?.map((p: any) => <SelectItem key={p.id} value={p.id}>{p.product_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Employee</Label>
                <Select value={autoFillEmp} onValueChange={handleAutoFillEmp}>
                  <SelectTrigger className="bg-white text-black h-9 border-none"><SelectValue placeholder="choose Employee" /></SelectTrigger>
                  <SelectContent>{employees?.map((e: any) => <SelectItem key={e.id} value={e.id}>{e.employee_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Main Input Grid for rows */}
          <div className="space-y-4 mb-6">
            {creditItems.map((item, index) => (
              <div key={item.id} className="relative border border-white/20 p-4 rounded-xl bg-blue-700/30">
                <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_2fr_1fr_1fr_1fr_1fr_1fr_1fr] gap-3 mb-4 items-end">
                  <div className="flex flex-col gap-1">
                    <Label className="text-white text-xs">Organization</Label>
                    <Select value={item.organization} onValueChange={v => updateItem(item.id, 'organization', v)}>
                      <SelectTrigger className="bg-white text-black h-9 border-none"><SelectValue placeholder="Choose Organization" /></SelectTrigger>
                      <SelectContent>{customers?.map((c: any) => <SelectItem key={c.id} value={c.id}>{c.organization_name}</SelectItem>)}</SelectContent>
                    </Select>
                    {item.organization && balances[item.organization] !== undefined && (
                      <span className="text-[10px] text-amber-300 font-extrabold mt-1">
                        Pend Bal: {balances[item.organization].toFixed(3)}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-white text-xs">Vehicle No</Label>
                    <Input placeholder="0" value={item.vehicleNo} onChange={e => updateItem(item.id, 'vehicleNo', e.target.value)} className="bg-white text-black h-9 border-none" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-white text-xs">Product</Label>
                    <Select value={item.product} onValueChange={v => updateItem(item.id, 'product', v)}>
                      <SelectTrigger className="bg-white text-black h-9 border-none"><SelectValue placeholder="choose Product" /></SelectTrigger>
                      <SelectContent>{fuelProducts?.map((p: any) => <SelectItem key={p.id} value={p.id}>{p.product_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-white text-xs">Price</Label>
                    <Input placeholder="Price" type="number" value={item.price} onChange={e => updateItem(item.id, 'price', e.target.value)} className="bg-white text-black h-9 border-none" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-white text-xs">Credit Amt</Label>
                    <Input placeholder="Credit Amt" type="number" step="0.01" value={item.creditAmt} onChange={e => updateItem(item.id, 'creditAmt', e.target.value)} className="bg-white text-black h-9 border-none" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-white text-xs">Discount</Label>
                    <Input placeholder="0" type="number" value={item.discount} onChange={e => updateItem(item.id, 'discount', e.target.value)} className="bg-white text-black h-9 border-none" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-white text-xs">Qty (Lts)</Label>
                    <Input placeholder="0" type="number" value={item.qty} onChange={e => updateItem(item.id, 'qty', e.target.value)} className="bg-white text-black h-9 border-none" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-white text-xs">Indent No</Label>
                    <Input placeholder="0" value={item.indentNo} onChange={e => updateItem(item.id, 'indentNo', e.target.value)} className="bg-white text-black h-9 border-none" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-white text-xs">Misc.Charges</Label>
                    <Input placeholder="0" type="number" value={item.miscCharges} onChange={e => updateItem(item.id, 'miscCharges', e.target.value)} className="bg-white text-black h-9 border-none" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_2fr_1fr_2fr_1fr_1fr_1fr] gap-3 items-end">
                  <div className="flex flex-col gap-1">
                    <Label className="text-white text-xs">Bill no.</Label>
                    <Input placeholder="0" value={item.billNo} onChange={e => updateItem(item.id, 'billNo', e.target.value)} className="bg-white text-black h-9 border-none" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-white text-xs">Upload Image</Label>
                    <input id={`file-upload-credit-${item.id}`} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileUpload(item.id, e.target.files?.[0])} />
                    <div
                      className="bg-white rounded h-9 flex items-center px-1 cursor-pointer transition-colors border-none"
                      onClick={() => triggerFileInput(item.id)}
                    >
                      <div className="h-7 px-3 flex items-center justify-center text-xs bg-[#4ca1ff] hover:bg-[#3498db] text-white rounded font-medium shadow-sm transition">Browse...</div>
                      <span className="text-[9px] text-gray-500 ml-1 truncate flex-1 block overflow-hidden leading-tight">
                        {item.fileName || "Allowed (JPEG, JPG, PNG)\nMaxSize:2MB"}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-white text-xs">Employee</Label>
                    <Select value={item.employee} onValueChange={v => updateItem(item.id, 'employee', v)}>
                      <SelectTrigger className="bg-white text-black h-9 border-none"><SelectValue placeholder="Employee" /></SelectTrigger>
                      <SelectContent>{employees?.map((e: any) => <SelectItem key={e.id} value={e.id}>{e.employee_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-white text-xs">Effect</Label>
                    <Select value={item.effect} onValueChange={v => updateItem(item.id, 'effect', v)}>
                      <SelectTrigger className="bg-white text-black h-9 border-none"><SelectValue placeholder="No" /></SelectTrigger>
                      <SelectContent><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-white text-xs">Description</Label>
                    <Input placeholder="Description" value={item.description} onChange={e => updateItem(item.id, 'description', e.target.value)} className="bg-white text-black h-9 border-none" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-white text-xs">Meter.Read</Label>
                    <Input placeholder="0" value={item.meterRead} onChange={e => updateItem(item.id, 'meterRead', e.target.value)} className="bg-white text-black h-9 border-none" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-white text-xs">Milleage</Label>
                    <Input placeholder="0" value={item.mileage} onChange={e => updateItem(item.id, 'mileage', e.target.value)} className="bg-white text-black h-9 border-none" />
                  </div>
                  <div className="flex justify-center gap-2 pb-1">
                    {!editingId && (
                      <Button size="icon" className="bg-transparent border border-white text-white hover:bg-white/20 rounded-full h-8 w-8" onClick={handleAddItem}>
                        <Plus className="h-5 w-5" />
                      </Button>
                    )}
                    {creditItems.length > 1 && !editingId && (
                      <Button size="icon" className="bg-red-500 hover:bg-red-600 border-none text-white rounded-full h-8 w-8" onClick={() => handleRemoveItem(item.id)}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {item.effect === "Yes" && (
                  <div className="flex flex-col gap-1 w-[200px] mt-2">
                    <Label className="text-white text-xs">Effect To</Label>
                    <Input placeholder="Effect To" value={item.effectTo} onChange={e => updateItem(item.id, 'effectTo', e.target.value)} className="bg-white text-black h-9 border-none" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Confirm & Cancel Buttons */}
          <div className="flex justify-center mt-2 gap-4">
            {editingId && (
              <Button onClick={handleCancelEdit} className="bg-gray-500 hover:bg-gray-600 text-white font-bold px-12 rounded-full shadow-lg transform transition active:scale-95">
                CANCEL
              </Button>
            )}
            <Button onClick={handleConfirm} disabled={createCreditSale.isPending || updateCreditSale.isPending} className="bg-[#84cc16] hover:bg-[#65a30d] text-white font-bold px-12 rounded-full shadow-lg transform transition active:scale-95">
              {createCreditSale.isPending || updateCreditSale.isPending ? "SAVING..." : (editingId ? "UPDATE" : "CONFIRM")}
            </Button>
          </div>

        </div>
      </Card>

      {/* Filter Bar */}
      <div className="bg-white p-3 rounded-lg shadow-sm mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-600">Search From</span>
          <Input type="date" value={searchFrom} onChange={e => setSearchFrom(e.target.value)} className="w-32 h-8" />
        </div>
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-600">To</span>
          <Input type="date" value={searchTo} onChange={e => setSearchTo(e.target.value)} className="w-32 h-8" />
        </div>
        <div className="flex items-center gap-2 flex-1">
          <span className="font-semibold text-gray-600">Organization</span>
          <Select value={searchOrg} onValueChange={setSearchOrg}>
            <SelectTrigger className="w-48 h-8"><SelectValue placeholder="" /></SelectTrigger>
            <SelectContent>{customers?.map((c: any) => <SelectItem key={c.id} value={c.id}>{c.organization_name}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={() => refetch()} className="bg-orange-500 hover:bg-orange-600 text-white h-8 px-4"><Search className="w-4 h-4 mr-1" /> Search</Button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2 mb-2">
        <Button variant="outline" className="text-red-500 border-red-200 bg-red-50 h-8" onClick={handleDelete}><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
        <Button variant="outline" className="text-blue-600 border-blue-200 bg-blue-50 h-8" onClick={handlePrint}><Printer className="w-4 h-4 mr-1" /> Direct Print</Button>
        <Button variant="outline" className="text-blue-600 border-blue-200 bg-blue-50 h-8" onClick={() => toast({ title: "Info", description: "Feature coming soon" })}><FileText className="w-4 h-4 mr-1" /> Add to Billing</Button>
        <Button variant="outline" className="text-blue-600 border-blue-200 bg-blue-50 h-8" onClick={() => toast({ title: "Info", description: "Feature coming soon" })}><Send className="w-4 h-4 mr-1" /> Send SMS</Button>

        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-500">Filter:</span>
          <Input placeholder="Type to filter..." value={filterText} onChange={e => setFilterText(e.target.value)} className="h-8 w-48" />
        </div>
      </div>

      {/* Filter options (Show All) */}
      <div className="flex items-center gap-2 mb-2 text-sm">
        <span className="text-gray-500">Show:</span>
        <Select defaultValue="all">
          <SelectTrigger className="w-24 h-7"><SelectValue placeholder="All" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All</SelectItem></SelectContent>
        </Select>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-xs text-left border-collapse">
          <thead className="bg-gray-50 text-gray-700 font-semibold border-b">
            <tr>
              <th className="p-2 border-r">
                <input
                  type="checkbox"
                  checked={creditSalesRows.length > 0 && selectedRows.size === creditSalesRows.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedRows(new Set(creditSalesRows.map((row: any) => row.id)));
                    } else {
                      setSelectedRows(new Set());
                    }
                  }}
                />
              </th>
              <th className="p-2 border-r">S.No</th>
              <th className="p-2 border-r">Txn No</th>
              <th className="p-2 border-r">Date</th>
              <th className="p-2 border-r">Shift</th>
              <th className="p-2 border-r">Org/Cust.</th>
              <th className="p-2 border-r">Vehicle No</th>
              <th className="p-2 border-r">Product</th>
              <th className="p-2 border-r">Employee</th>
              <th className="p-2 border-r">Quantity</th>
              <th className="p-2 border-r">Price</th>
              <th className="p-2 border-r">Cost (₹)</th>
              <th className="p-2 border-r">Discount (₹)</th>
              <th className="p-2 border-r">Indent No</th>
              <th className="p-2 border-r">Bill No</th>
              <th className="p-2 border-r">Advance</th>
              <th className="p-2 border-r">Picture</th>
              <th className="p-2 border-r">Effect Asset</th>
              <th className="p-2 border-r">Mileage</th>
              <th className="p-2 border-r">Description</th>
              <th className="p-2 border-r text-center">Action</th>
              <th className="p-2 border-r">User Log Details</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filteredCreditSalesRows.map((row: any, i: number) => (
              <tr key={row.id} className="hover:bg-gray-50">
                <td className="p-2 border-r text-center">
                  <input type="checkbox" checked={selectedRows.has(row.id)} onChange={(e) => {
                    const s = new Set(selectedRows);
                    if (e.target.checked) {
                      s.add(row.id);
                    } else {
                      s.delete(row.id);
                    }
                    setSelectedRows(s);
                  }} />
                </td>
                <td className="p-2 border-r text-center text-gray-500">{i + 1}</td>
                <td className="p-2 border-r font-mono text-blue-600">{row.transaction_number || '-'}</td>
                <td className="p-2 border-r whitespace-nowrap">{formatDate(row.sale_date)}</td>
                <td className="p-2 border-r text-center">{row.shift}</td>
                <td className="p-2 border-r font-medium">{row.organization_name}</td>
                <td className="p-2 border-r text-center">{row.vehicle_number || '0'}</td>
                <td className="p-2 border-r">{row.product_name}</td>
                <td className="p-2 border-r">{row.employee_name}</td>
                <td className="p-2 border-r text-right font-medium">{Number(row.quantity).toFixed(3)}</td>
                <td className="p-2 border-r text-right">{Number(row.price_per_unit).toFixed(3)}</td>
                <td className="p-2 border-r text-right font-bold text-green-600">{Number(row.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                <td className="p-2 border-r text-right text-red-500">{Number(row.discount).toFixed(3)}</td>
                <td className="p-2 border-r text-center">{row.indent_no || '0'}</td>
                <td className="p-2 border-r text-center">{row.bill_no || '0'}</td>
                <td className="p-2 border-r text-right">{Number(row.advance || 0).toFixed(3)}</td>
                <td className="p-2 border-r text-center text-gray-400">View</td>
                <td className="p-2 border-r text-center">{row.effect === 'Yes' ? (row.effect_to || 'Yes') : 'No'}</td>
                <td className="p-2 border-r text-center">{row.mileage || '0'}</td>
                <td className="p-2 border-r truncate max-w-[150px]">{row.description}</td>
                <td className="p-2 border-r text-center">
                  <div className="flex justify-center gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-600" onClick={() => handleEditRow(row)} title="Edit">
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => handleDeleteRow(row.id)} title="Delete">
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </td>
                <td className="p-2 border-r text-gray-600 text-[10px] min-w-48 whitespace-nowrap">
                  <div>Created: {row.created_by_name || "Super Admin"} {formatDateTime(row.created_at)}</div>
                  {row.updated_at && (new Date(row.updated_at).getTime() - new Date(row.created_at).getTime() > 1000) && (
                    <div>Modified: {row.created_by_name || "Super Admin"} {formatDateTime(row.updated_at)}</div>
                  )}
                </td>
              </tr>
            ))}
            {filteredCreditSalesRows.length === 0 && (
              <tr><td colSpan={22} className="p-8 text-center text-gray-500">No credit sales found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Dialog open={isAddPartyOpen} onOpenChange={setIsAddPartyOpen}>
        <DialogContent className="sm:max-w-4xl p-0 overflow-hidden bg-white">
          <DialogHeader className="bg-[#0056b3] p-4 text-white flex flex-row justify-between items-center space-y-0">
            <DialogTitle className="text-lg">Add Credit Organization</DialogTitle>
          </DialogHeader>
          <div className="p-6 grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="flex flex-col gap-2">
              <Label className="font-bold flex">Org.Name <span className="text-red-500 ml-1">*</span></Label>
              <Input
                placeholder="Org Name"
                value={newParty.orgName}
                onChange={e => setNewParty({ ...newParty, orgName: e.target.value })}
                className="bg-white border-gray-300"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label className="font-bold flex">Cust.Name <span className="text-red-500 ml-1">*</span></Label>
              <Input
                placeholder="Customer Name"
                value={newParty.custName}
                onChange={e => setNewParty({ ...newParty, custName: e.target.value })}
                className="bg-white border-gray-300"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label className="font-bold">Phone No</Label>
              <Input
                placeholder="Contact No"
                value={newParty.phone}
                onChange={e => setNewParty({ ...newParty, phone: e.target.value })}
                className="bg-white border-gray-300"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label className="font-bold">Email</Label>
              <Input
                placeholder="Email Id"
                value={newParty.email}
                onChange={e => setNewParty({ ...newParty, email: e.target.value })}
                className="bg-white border-gray-300"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label className="font-bold">Credit Limit</Label>
              <Input
                placeholder="Credit limit"
                value={newParty.creditLimit}
                onChange={e => setNewParty({ ...newParty, creditLimit: e.target.value })}
                className="bg-white border-gray-300"
              />
            </div>
          </div>
          <DialogFooter className="p-4 bg-gray-50 flex justify-center gap-2 sm:justify-center">
            <Button
              className="bg-green-500 hover:bg-green-600 text-white font-bold w-24"
              onClick={handleAddParty}
            >
              <span className="mr-1">✔</span> Add
            </Button>
            <Button
              className="bg-red-500 hover:bg-red-600 text-white font-bold w-24"
              onClick={() => setIsAddPartyOpen(false)}
            >
              <span className="mr-1">✖</span> Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

